import { UserAvatar } from "@/modern/components/user-avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { useState, useEffect, useRef, useCallback } from "react";
import type { BoardUser } from "@/react/hooks/entityHooks/useEntityOwnership";
import { useLiveUsers } from "@/react/hooks/useLiveUsers";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

interface OverflowUsersPopoverProps {
  users: Array<BoardUser>;
}

const OverflowUsersPopover = ({ users }: OverflowUsersPopoverProps) => {
  const [open, setOpen] = useState(false);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimeouts = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
    }
    openTimeoutRef.current = setTimeout(() => {
      setOpen(true);
      openTimeoutRef.current = null;
    }, 300);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 200);
  }, []);

  useEffect(() => {
    return clearTimeouts;
  }, [clearTimeouts]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="h-7 w-7 truncate rounded-full flex items-center justify-center cursor-pointer transition-colors z-10 bg-background ring-1 ring-border"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">+{users.length}</span>
        </div>
      </PopoverTrigger>
      <PopoverContent side="top" align="center" className="w-64 p-0" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="max-h-64 overflow-auto p-2 space-y-1">
          {users.map((user) => {
            return (
              <div key={user.id} className="p-2 hover:bg-accent transition-colors rounded-md">
                <UserAvatar user={user} size="small" showName={true} />
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const AppActiveUsers = () => {
  const { entityManager } = useSwivuBryntumContext();
  // Get active users from the hook - it handles all context access and mapping
  const boardUsers = useLiveUsers();

  // Only render when dashboard view is open or
  // Don't render if no active users
  if (!entityManager.isOpen || !boardUsers.length) {
    return null;
  }

  const displayedUsers = boardUsers.slice(0, 3);
  const overflowUsers = boardUsers.slice(3);

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center -space-x-2">
        {/* Show first 3 users with overlapping */}
        {displayedUsers.map((user) => (
          <UserAvatar key={user.id} user={user} size="small" showTooltip={true} showRemove={false} />
        ))}
        {/* Show +N badge if more than 3 users */}
        {overflowUsers.length > 0 && <OverflowUsersPopover users={overflowUsers} />}
      </div>
    </div>
  );
};